/**
 * 入口：按依赖顺序加载 legacy JSX 和数据，最后挂载 App。
 *
 * 现有代码通过 window 全局共享（window.SiteData、window.I18N、window.__KexiApp 等）。
 * Stage 1 保持这种风格不动，后续逐步拆为模块化 ESM 组件。
 */
import React from "react";
import ReactDOM from "react-dom/client";

import "./styles/globals.css";

// 1) 全局数据 & i18n 资源（写到 window.*）
import "./90-/data.js";
import "./90-/i18n-zh.js";
import "./90-/i18n-en.js";

// 2) 认证（useAuth/authFetch/AuthModal 到 window）+ 公共组件（shell.jsx 暴露 Nav/Footer/Logo/etc）
import "./90-/captcha-slider.jsx"; // 拼图拖动人机验证（auth 表单依赖 window.SliderCaptcha）
import "./90-/auth.jsx";
import "./90-/shell.jsx";

// 3) 各页面（每个 jsx 把页面组件挂到 window）
import "./90-/pages-main.jsx";
import "./90-/pages-portfolio.jsx";
import "./90-/pages-aux.jsx";
import "./90-/pages-studio.jsx";
import "./90-/pages-dashboard.jsx";

// 4) 顶层 App
import "./90-/app.jsx";

// 5) 后台「实时预览」内联编辑模式（仅 ?edit=1 时激活，普通访客零影响）
import "./90-/edit-mode.jsx";

const App = (window as any).__KexiApp as React.ComponentType;

/**
 * 远程文案：启动时拉 /api/content，深合并进 window.SiteData / window.I18N。
 * 注意：各页面模块顶层持有 `const D = window.SiteData` 的引用，
 * 必须原地 mutate，不能替换对象引用。
 * API 不可用时静默降级，使用打包内默认文案。
 */
function deepMergeInPlace(target: any, source: any) {
  for (const k of Object.keys(source)) {
    const sv = source[k];
    const tv = target[k];
    if (
      sv && typeof sv === "object" && !Array.isArray(sv) &&
      tv && typeof tv === "object" && !Array.isArray(tv)
    ) {
      deepMergeInPlace(tv, sv);
    } else {
      // 数组与标量整体替换（后台保存的是完整数组）
      target[k] = sv;
    }
  }
}

async function loadRemoteContent() {
  try {
    // 编辑模式（?edit=1）下绕过 HTTP 缓存，避免保存后重载读到旧的缓存文案
    const editing = new URLSearchParams(location.search).get("edit") === "1";
    const res = await fetch("/api/content", {
      signal: AbortSignal.timeout(4000),
      cache: editing ? "no-store" : "default",
    });
    if (!res.ok) return;
    const data = await res.json();
    if (data?.site_data) deepMergeInPlace((window as any).SiteData, data.site_data);
    if (data?.i18n) deepMergeInPlace((window as any).I18N, data.i18n);
  } catch {
    // 静默降级：用打包内默认文案
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found in index.html");
loadRemoteContent().finally(() => {
  ReactDOM.createRoot(rootEl).render(<App />);
});
